import React, { useState, useContext } from "react";
import headerDeleteDarkIcon from "../../../images/Builder/delete_bordered_dark.svg";
import headerDeleteIcon from "../../../images/Builder/delete_bordered.svg";
import { useTheme } from "../../../context/ThemeContext";
import WebsocketWrapperContext from "../../../context/WebsocketWrapperContext";
import { useSavedStrategies } from "../context/SavedStrategiesContext";
import { toast } from "react-toastify";
import { getCookie } from "../../../utils/cookieUtils";

// const generateExpiryLabels = (optionExpiries) => {
//   const labels = [];
//   const currentDate = new Date();
//   optionExpiries.forEach((expiry, index) => {
//     const expiryDate = new Date(expiry);
//     const diffDays = Math.ceil(
//       (expiryDate - currentDate) / (1000 * 60 * 60 * 24)
//     );
//     const diffWeeks = Math.ceil(diffDays / 7);

//     if (diffWeeks <= 1) {
//       labels.push("CW");
//     } else if (diffWeeks <= 4) {
//       labels.push(`CW + ${diffWeeks - 1}`);
//     } else if (diffWeeks == 5) {
//       labels.push("CM");
//     }
//     else {
//       labels.push(`CM + ${Math.floor((diffWeeks - 4) / 4)}`);
//     }
//   });

//   return labels;
// };

const generateExpiryLabels = (optionExpiries) => {
  // Fixed expiry labels in the specified order with user-friendly display names
  // The 'value' field contains the internal abbreviated labels (CW, CM, etc.)
  // The 'display' field contains the user-friendly names shown in the dropdown
  const defaultLabels = [
    { value: "CW", display: "Current Week" },
    { value: "CW + 1", display: "Current Week + 1" },
    { value: "CW + 2", display: "Current Week + 2" },
    { value: "CW + 3", display: "Current Week + 3" },
    { value: "CM", display: "Current Month" },
    { value: "CW + 4", display: "Current Week + 4" },
    { value: "CW + 5", display: "Current Week + 5" },
    { value: "CW + 6", display: "Current Week + 6" },
    { value: "CM + 1", display: "Current Month + 1" },
    { value: "CW + 7", display: "Current Week + 7" },
    { value: "CW + 8", display: "Current Week + 8" },
    { value: "CW + 9", display: "Current Week + 9" },
    { value: "CM + 2", display: "Current Month + 2" },
    { value: "CM + 3", display: "Current Month + 3" }
  ];

  // Return labels with value, compact (internal value) and full (display) versions for the dropdown
  return defaultLabels.map(label => ({
    value: label.value,
    compact: label.value,
    full: label.display
  }));
};

// Helper function to map actual expiry dates to appropriate labels
const mapExpiryToLabel = (expiryDate, expiries, isFuture = false) => {
  const currentDate = new Date();
  const expiry = new Date(expiryDate);

  // Find the index of this expiry in the expiries array
  const expiryIndex = expiries.findIndex(exp => {
    const expDate = new Date(exp);
    return Math.abs(expDate.getTime() - expiry.getTime()) < 24 * 60 * 60 * 1000; // Within 1 day
  });

  if (expiryIndex === -1) {
    return "CW"; // Default fallback
  }

  // Special handling for futures: if it's the last date of the month, use CM
  if (isFuture) {
    const currentMonth = expiry.getMonth();
    const currentYear = expiry.getFullYear();

    // Get all expiries for this month
    const monthExpiries = expiries.filter(exp => {
      const expDate = new Date(exp);
      return expDate.getMonth() === currentMonth && expDate.getFullYear() === currentYear;
    });

    // Check if this is the last expiry of its month
    const isLastExpiryOfMonth = monthExpiries.length > 0 &&
      expiry.getTime() === Math.max(...monthExpiries.map(d => new Date(d).getTime()));

    if (isLastExpiryOfMonth) {
      // Calculate month difference from current date
      const monthDiff = (currentYear - currentDate.getFullYear()) * 12 + (currentMonth - currentDate.getMonth());

      if (monthDiff === 0) {
        return "CM"; // Current month
      } else if (monthDiff === 1) {
        return "CM + 1";
      } else if (monthDiff === 2) {
        return "CM + 2";
      } else if (monthDiff === 3) {
        return "CM + 3";
      } else {
        return `CM + ${monthDiff}`;
      }
    }
  }

  // Use the same mapping logic as in ApplyPrebuilt.jsx
  // Map the actual expiry index to the corresponding label
  const defaultLabels = [
    "CW", "CW + 1", "CW + 2", "CW + 3", "CM", "CW + 4", "CW + 5",
    "CW + 6", "CM + 1", "CW + 7", "CW + 8", "CW + 9", "CM + 2", "CM + 3"
  ];

  // For the first few expiries, use weekly labels
  if (expiryIndex < 4) {
    return defaultLabels[expiryIndex] || "CW";
  }

  // For monthly expiries, we need to check if this is the last expiry of its month
  const currentMonth = expiry.getMonth();
  const currentYear = expiry.getFullYear();

  // Get all expiries for this month
  const monthExpiries = expiries.filter(exp => {
    const expDate = new Date(exp);
    return expDate.getMonth() === currentMonth && expDate.getFullYear() === currentYear;
  });

  // Check if this is the last expiry of its month
  const isLastExpiryOfMonth = monthExpiries.length > 0 &&
    expiry.getTime() === Math.max(...monthExpiries.map(d => new Date(d).getTime()));

  if (isLastExpiryOfMonth) {
    // Calculate month difference from current date
    const monthDiff = (currentYear - currentDate.getFullYear()) * 12 + (currentMonth - currentDate.getMonth());

    // Special case: If current week is also the last expiry of current month, treat it as current week
    if (monthDiff === 0) {
      const diffDays = Math.ceil((expiry - currentDate) / (1000 * 60 * 60 * 24));
      const diffWeeks = Math.floor(diffDays / 7);

      if (diffWeeks <= 0) {
        return "CW";
      } else {
        return "CM";
      }
    } else if (monthDiff === 1) {
      return "CM + 1";
    } else if (monthDiff === 2) {
      return "CM + 2";
    } else if (monthDiff === 3) {
      return "CM + 3";
    } else {
      return `CM + ${monthDiff}`;
    }
  } else {
    // For non-last expiries, use weekly classification
    const diffDays = Math.ceil((expiry - currentDate) / (1000 * 60 * 60 * 24));
    const diffWeeks = Math.floor(diffDays / 7);

    if (diffWeeks <= 0) {
      return "CW";
    } else {
      return `CW + ${diffWeeks}`;
    }
  }
};

const SaveStrategyModal = ({
  strategyData,
  onClose,
  onSave,
  isEditMode = false,
  existingStrategyId = null,
  existingStrategyName = "",
}) => {
  const { symbolData, subscribeToSymbols } = useContext(
    WebsocketWrapperContext
  );
  const { theme } = useTheme();
  const { refreshSavedStrategies } = useSavedStrategies();
  const [strategyName, setStrategyName] = useState(existingStrategyName || "");
  const [isLoading, setIsLoading] = useState(false);

  const [openExpiryDropdownIndex, setOpenExpiryDropdownIndex] = useState(null);

  const client_id = getCookie("client_id");
  const selectedSymbol = strategyData?.selectedSymbol;

  // Generate expiry labels based on row type (future vs options)
  const getExpiryLabelsForRow = (isFuture) => {
    if (isFuture) {
      return selectedSymbol?.fut_expiries
        ? generateExpiryLabels(selectedSymbol.fut_expiries)
        : [];
    } else {
      return selectedSymbol?.option_expiries
        ? generateExpiryLabels(selectedSymbol.option_expiries)
        : [];
    }
  };

  const [rows, setRows] = useState(() => {
    // For edit mode, use the strategy data directly
    if (isEditMode && strategyData.legs) {
      return strategyData.legs.map((leg, index) => {
        // Handle both old numeric indices and new label values
        let expiryValue = leg.expiries || leg.expiryIndex || "CW";

        // If it's a numeric value, convert it to the corresponding label
        if (typeof expiryValue === 'number') {
          const defaultLabels = [
            "CW", "CW + 1", "CW + 2", "CW + 3", "CM", "CW + 4", "CW + 5",
            "CW + 6", "CM + 1", "CW + 7", "CW + 8", "CW + 9", "CM + 2", "CM + 3"
          ];
          expiryValue = defaultLabels[expiryValue] || "CW";
        }

        return {
          id: index,
          isBuy: leg.isBuy,
          isCALL: leg.isCALL,
          isFuture: leg.isFuture,
          lotSize: leg.lotSize,
          atmIndex: leg.atmIndex,
          expiryIndex: expiryValue,
        };
      });
    }

    // For save mode, calculate ATM offset (existing logic)
    const legs = Array.isArray(strategyData)
      ? strategyData
      : strategyData.legs || [];

    return legs.map((leg, index) => {
      let atmOffset = 0;
      let expiryIndex = 0;

      try {
        // const selectedSymbol = strategyData?.selectedSymbol;

        // //expiry
        // const expiryLabels = selectedSymbol?.option_expiries
        //   ? generateExpiryLabels(selectedSymbol.option_expiries)
        //   : [];

        if (selectedSymbol && symbolData && strategyData.allOptionsData) {
          const ltp = parseFloat(symbolData[selectedSymbol.code]?.ltp);
          const optionsData =
            strategyData.allOptionsData[selectedSymbol.scrip]?.[leg.expiry]
              ?.options;

          // Find expiry label using mapping function
          // Use fut_expiries for futures, option_expiries for options
          const expiryArray = leg.isFuture
            ? selectedSymbol.fut_expiries
            : selectedSymbol.option_expiries;

          if (expiryArray && leg.expiry) {
            const mappedLabel = mapExpiryToLabel(leg.expiry, expiryArray, leg.isFuture);
            // Use the mapped label directly as the expiryIndex value
            expiryIndex = mappedLabel;
          } else {
            // Fallback to "CW" if no expiry mapping is available
            expiryIndex = "CW";
          }

          if (ltp && optionsData?.length) {
            // Find ATM strike index
            let minDiff = Math.abs(parseFloat(optionsData[0].strike) - ltp);
            let atmIndex = 0;

            for (let i = 1; i < optionsData.length; i++) {
              const diff = Math.abs(parseFloat(optionsData[i].strike) - ltp);
              if (diff < minDiff) {
                minDiff = diff;
                atmIndex = i;
              }
            }

            // Find current leg's index
            const legStrike = parseFloat(leg.strike);
            for (let i = 0; i < optionsData.length; i++) {
              if (parseFloat(optionsData[i].strike) === legStrike) {
                atmOffset = i - atmIndex;
                break;
              }
            }
          }
        }
      } catch (e) {
        console.error("ATM calculation error", e);
      }

      if (!leg?.isCALL) {
        atmOffset = atmOffset * -1;
      }
      return {
        id: index,
        isBuy: leg.isBuy,
        isCALL: leg.isCALL,
        isFuture: leg.isFuture,
        lotSize: leg.lotSize,
        atmIndex: atmOffset,
        expiryIndex: expiryIndex,
      };
    });
  });

  // 1. Add helper function to generate expiry labels (add this before the SaveStrategyModal component)

  const handleSave = async () => {
    if (strategyName.trim()) {
      setIsLoading(true);
      try {
        // object
        const strategyLegs = Array.isArray(strategyData)
          ? strategyData
          : strategyData.legs || [];

        let selectedSymbol = strategyData?.selectedSymbol;
        let buy_sell_list = [];
        let call_put_list = [];
        let lots_list = [];
        let atmindex_list = [];

        let ltp = parseFloat(symbolData[selectedSymbol?.["code"]]?.ltp);
        let optionsData =
          strategyData.allOptionsData[selectedSymbol?.["scrip"]]?.[
          strategyData?.legs[0]?.["expiry"]
          ]?.["options"];

        // Find the ATM strike (closest to LTP)
        let atmIndex = 0;
        let minDifference = Math.abs(parseFloat(optionsData?.[0].strike) - ltp);

        for (let i = 1; i < optionsData?.length; i++) {
          const currentStrike = parseFloat(optionsData[i].strike);
          const difference = Math.abs(currentStrike - ltp);

          if (difference < minDifference) {
            minDifference = difference;
            atmIndex = i;
          }
        }
        strategyData["legs"].forEach((leg) => {
          buy_sell_list.push(leg.isBuy ? "Buy" : "Sell");
          call_put_list.push(leg.isFuture ? "Fut" : (leg.isCALL ? "Call" : "Put"));
          lots_list.push(leg.lotSize);

          let targetStrike = parseFloat(leg["strike"]);
          let targetIndex = -1;
          for (let i = 0; i < optionsData?.length; i++) {
            if (parseFloat(optionsData[i].strike) === targetStrike) {
              targetIndex = i;
              break;
            }
          }
          let offset = targetIndex - atmIndex;
          atmindex_list.push(offset);
        });

        // Convert label values back to indices for API
        const defaultLabels = [
          "CW", "CW + 1", "CW + 2", "CW + 3", "CM", "CW + 4", "CW + 5",
          "CW + 6", "CM + 1", "CW + 7", "CW + 8", "CW + 9", "CM + 2", "CM + 3"
        ];

        let required_format = {
          buy_sell_list: rows.map((row) => (row.isBuy ? "Buy" : "Sell")),
          call_put_list: rows.map((row) => (row.isFuture ? "Fut" : (row.isCALL ? "Call" : "Put"))),
          lots_list: rows.map((row) => row.lotSize),
          atmindex_list: rows.map((row) => row.atmIndex),
          expiries: rows.map((row) => {
            // Convert label value to index for API
            const index = defaultLabels.indexOf(row.expiryIndex);
            return index !== -1 ? index : 0; // fallback to first option
          }),
        };

        const payload = isEditMode
          ? {
            user_id: client_id,
            id: existingStrategyId,
            strategy: required_format,
            name: strategyName,
          }
          : {
            strategy: required_format,
            name: strategyName,
            user_id: client_id,
          };

        // Different API endpoint for edit vs save
        const apiUrl = isEditMode
          ? "https://qb1a0x5wrd.execute-api.ap-south-1.amazonaws.com/default/editSavedStrategy"
          : "https://4vnqs2waq4.execute-api.ap-south-1.amazonaws.com/default/saveStrategy1lyoptions";

        // API call
        const response = await fetch(apiUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        });

        if (response.ok) {
          const result = await response.json();
          console.log(
            `Strategy ${isEditMode ? "updated" : "saved"} successfully:`,
            result
          );

          toast(`Strategy ${isEditMode ? "updated" : "saved"} successfully!`, {
            position: "bottom-center",
            autoClose: 1000,
            hideProgressBar: true,
            closeOnClick: false,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: "dark",
            className: "custom-snackbar",
            bodyClassName: "custom-snackbar-body",
          });

          await refreshSavedStrategies();
          onSave(strategyName, rows);

          onClose();
        } else {
          console.error("Failed to save strategy:", response.statusText);
        }
      } catch (error) {
        console.error("Error saving strategy:", error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  //andler function for expiry change (add this after other handler functions)
  const handleExpiryIndexChange = (index, value) => {
    setRows(
      rows.map((row, i) =>
        i === index ? { ...row, expiryIndex: value } : row
      )
    );
  };

  const handleDeleteRow = (index) => {
    setRows(rows.filter((_, i) => i !== index));
  };

  const handleDeleteAllRows = () => {
    setRows([]);
  };

  const toggleBuySell = (index) => {
    setRows(
      rows.map((row, i) => (i === index ? { ...row, isBuy: !row.isBuy } : row))
    );
  };

  const toggleCallPut = (index) => {
    setRows(
      rows.map((row, i) =>
        i === index ? { ...row, isCALL: !row.isCALL } : row
      )
    );
  };

  const handleLotSizeChange = (index, value) => {
    setRows(
      rows.map((row, i) => (i === index ? { ...row, lotSize: value } : row))
    );
  };

  const handleAtmIndexChange = (index, value) => {
    const numValue = value === "" ? "" : parseInt(value) || 0;
    setRows(
      rows.map((row, i) => (i === index ? { ...row, atmIndex: numValue } : row))
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[500]">
      <div className="bg-modalBg rounded-lg w-[500px] h-[400px] flex flex-col">
        <div className="flex items-center justify-between px-3 py-4 flex-shrink-0">
          <h2 className="text-base font-semibold text-p300">
            {isEditMode ? "Edit strategy" : "Save strategy"}
          </h2>
          <button
            onClick={onClose}
            className="btn btn-xs btn-circle hover:text-p400 bg-p400 border-n200 text-p300">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <div className="mb-4 px-4 flex-shrink-0">
          <input
            type="text"
            value={strategyName}
            onChange={(e) => setStrategyName(e.target.value)}
            placeholder="Name of strategy"
            className="w-full px-3 h-9 py-2 border border-n200 rounded-md bg-p400 font-medium text-sm text-p300"
            disabled={isLoading}
          />
        </div>

        <div className="w-full bg-modalBg rounded-md flex-1 flex flex-col min-h-0">
          <div className="w-full flex flex-col min-h-0">
            {/* Header */}
            <div className="flex items-center justify-between border border-n100 border-opacity-10 px-4 py-2 mb-2 flex-shrink-0">
              <span className="w-[26px] text-[10px] font-medium text-n500 text-center">
                B/S
              </span>
              <span className="text-[10px] font-medium text-n500 text-center -ml-4">
                Type
              </span>
              <span className="text-[10px] font-medium text-n500 text-center -ml-2">
                Lots
              </span>
              <span className="text-[10px] font-medium text-n500 text-center">
                Expiry
              </span>
              <span className="text-[10px] font-medium text-n500 text-center"></span>
              <span className="text-[10px] font-medium text-n500 text-center">
                ATM
              </span>
              <div className="cursor-pointer" onClick={handleDeleteAllRows}>
                <img
                  src={
                    theme === "dark" ? headerDeleteDarkIcon : headerDeleteIcon
                  }
                  alt="delete"
                  width={20}
                  height={20}
                />
              </div>
            </div>

            {/* Rows */}
            <div className="flex flex-col gap-1 overflow-y-auto flex-1">
              {rows.map((row, ind) => (
                <div
                  key={row.id}
                  className={`flex items-center justify-between text-center px-4 py-1 gap-4  ${ind % 2 !== 0 ? "bg-n000" : ""
                    }`}>
                  {/* Buy/Sell Button */}
                  <div
                    className={`w-[26px] h-[26px] flex items-center justify-center rounded text-white text-[10px] font-bold cursor-pointer ${row.isBuy ? "bg-p150" : "bg-red"
                      }`}
                    onClick={() => toggleBuySell(ind)}>
                    {row.isBuy ? "B" : "S"}
                  </div>

                  {/* Call/Put Button */}
                  <div
                    className={`flex items-center justify-center w-[26px] h-[26px] rounded border-[1.5px] text-[10px] text-p300 font-medium cursor-pointer ${row.isFuture
                      ? "bg-n300 text-p300 border-transparent"
                      : row.isCALL
                        ? "border-p150"
                        : "border-red"
                      }`}
                    onClick={() => !row.isFuture && toggleCallPut(ind)}>
                    {row.isFuture ? "FUT" : row.isCALL ? "CE" : "PE"}
                  </div>

                  {/* Lots Input */}
                  <input
                    type="number"
                    value={row.lotSize || ""}
                    onChange={(e) => handleLotSizeChange(ind, parseInt(e.target.value) || 0)}
                    placeholder="Lots"
                    min="1"
                    className="w-[55px] px-2 py-[6px] text-[10px] font-semibold border border-n300 rounded bg-p400 text-p300 text-center"
                  />

                  {/* Expiry Dropdown */}
                  <select
                    value={row.expiryIndex}
                    onChange={(e) => handleExpiryIndexChange(ind, e.target.value)}
                    className="h-[28px] w-32 px-2 py-1 border border-n300 rounded text-center bg-p400 text-[10px] font-medium text-p300 shadow-md"
                  >
                    {getExpiryLabelsForRow(row.isFuture).map((labelObj, index) => (
                      <option key={index} value={labelObj.value}>
                        {labelObj.full}
                      </option>
                    ))}
                  </select>

                  {/* ATM (constant) */}
                  {/* <div className="text-[10px] font-medium text-p300">ATM</div> */}

                  {/* ATM Index Input */}
                  <select
                    value={row.atmIndex}
                    onChange={(e) => handleAtmIndexChange(ind, Number(e.target.value))}
                    disabled={row.isFuture}
                    className={`h-[28px] w-24 px-2 py-1 border border-n300 rounded text-center text-[10px] font-medium shadow-md ${row.isFuture
                      ? 'bg-n200 text-n400 cursor-not-allowed'
                      : 'bg-p400 text-p300'
                      }`}
                  >
                    {Array.from({ length: 61 }, (_, i) => {
                      const value = i - 30;
                      let label = "";

                      if (value < 0) {
                        label = `ITM ${value}`;
                      } else if (value === 0) {
                        label = "ATM";
                      } else {
                        label = `OTM +${value}`;
                      }

                      return (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      );
                    })}
                  </select>

                  {/* Delete Row Button */}
                  <div
                    className="cursor-pointer"
                    onClick={() => handleDeleteRow(ind)}>
                    <img
                      src={
                        theme === "dark"
                          ? headerDeleteDarkIcon
                          : headerDeleteIcon
                      }
                      alt="delete"
                      width={20}
                      height={20}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3 px-4 py-4 flex-shrink-0">
          <button
            onClick={handleSave}
            className="px-4 py-2 bg-swipe-button text-p400 rounded-md font-semibold text-sm disabled:opacity-50"
            disabled={!strategyName.trim() || rows.length == 0 || isLoading}>
            {isLoading
              ? isEditMode
                ? "Updating..."
                : "Saving..."
              : isEditMode
                ? "Update"
                : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SaveStrategyModal;
